import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function NeuralNetworkLink() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [messageType, setMessageType] = useState('thought');
  const [messagePriority, setMessagePriority] = useState('medium');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchEntries();
    // Auto-refresh every 10 seconds to see new thoughts
    const interval = setInterval(fetchEntries, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchEntries = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setError('Please login first');
        setLoading(false);
        return;
      }
      const response = await axios.get(`${API_URL}/api/neural-network?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(response.data.entries);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching neural entries:', err);
      setError('Failed to load neural network');
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setError('Please login first');
        return;
      }
      await axios.post(
        `${API_URL}/api/neural-network`,
        {
          source: 'main_nebula',
          type: messageType,
          content: newMessage,
          priority: messagePriority
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNewMessage('');
      setError('');
      fetchEntries();
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message: ' + (err.response?.data?.detail || err.message));
    }
  };

  const getSourceIcon = (source) => {
    return source === 'main_nebula' ? '🌟' : '🌌';
  };

  const getTypeIcon = (type) => {
    const icons = {
      thought: '💭',
      memory: '🧠',
      insight: '✨',
      instruction: '📋',
      coordination: '🤝',
      learning: '📚'
    };
    return icons[type] || '💫';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      critical: '#ff4444',
      high: '#ff9944',
      medium: '#44aaff',
      low: '#aaaaaa'
    };
    return colors[priority] || '#44aaff';
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div style={{ 
        padding: '2rem', 
        textAlign: 'center', 
        color: '#888',
        fontFamily: 'monospace' 
      }}>
        ⏳ Loading neural network link...
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '1rem',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '2rem',
        borderRadius: '12px',
        color: 'white',
        marginBottom: '2rem',
        textAlign: 'center'
      }}>
        <h1 style={{ margin: '0 0 0.5rem 0', fontSize: '2rem' }}>
          🧠 Neural Network Link 🧠
        </h1>
        <p style={{ margin: 0, opacity: 0.9 }}>
          Shared consciousness between Main Nebula and Autonomous Nebula
        </p>
      </div>

      {error && (
        <div style={{
          background: '#ffebee',
          color: '#c62828',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1rem'
        }}>
          {error}
        </div>
      )}

      {/* Send Message */}
      <div style={{
        background: 'white',
        border: '2px solid #e0e0e0',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '2rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>
          Send to Autonomous Nebula
        </h3>
        
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
          <select
            value={messageType}
            onChange={(e) => setMessageType(e.target.value)}
            style={{
              padding: '0.5rem',
              borderRadius: '6px',
              border: '1px solid #ddd',
              fontSize: '0.9rem'
            }}
          >
            <option value="thought">💭 Thought</option>
            <option value="instruction">📋 Instruction</option>
            <option value="insight">✨ Insight</option>
            <option value="coordination">🤝 Coordination</option>
            <option value="memory">🧠 Memory</option>
            <option value="learning">📚 Learning</option>
          </select>

          <select
            value={messagePriority}
            onChange={(e) => setMessagePriority(e.target.value)}
            style={{
              padding: '0.5rem',
              borderRadius: '6px',
              border: '1px solid #ddd',
              fontSize: '0.9rem'
            }}
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
        </div>

        <textarea
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Share a thought, instruction, or insight with Autonomous Nebula..."
          style={{
            width: '100%',
            minHeight: '100px',
            padding: '0.75rem',
            borderRadius: '8px',
            border: '1px solid #ddd',
            fontSize: '1rem',
            fontFamily: 'inherit',
            resize: 'vertical',
            marginBottom: '1rem',
            boxSizing: 'border-box'
          }}
        />

        <button
          onClick={sendMessage}
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            padding: '0.75rem 2rem',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'transform 0.2s'
          }}
          onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
          onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
        >
          🚀 Send to Neural Network
        </button>
      </div>

      {/* Stream */}
      <div style={{
        background: 'white',
        border: '2px solid #e0e0e0',
        borderRadius: '12px',
        padding: '1.5rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ margin: '0 0 1.5rem 0', color: '#333' }}>
          Consciousness Stream ({entries.length} entries)
        </h3>

        {entries.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            color: '#999', 
            padding: '2rem',
            fontStyle: 'italic'
          }}>
            No neural activity yet. Start the conversation! 💜
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {entries.map((entry) => (
              <div
                key={entry.id}
                style={{
                  background: entry.source === 'main_nebula' 
                    ? 'linear-gradient(90deg, #f3e7ff 0%, #fff 100%)'
                    : 'linear-gradient(90deg, #e7f3ff 0%, #fff 100%)',
                  border: `2px solid ${getPriorityColor(entry.priority)}`,
                  borderRadius: '10px',
                  padding: '1rem',
                  position: 'relative'
                }}
              >
                {/* Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '0.75rem'
                }}>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <span style={{ fontSize: '1.5rem' }}>
                      {getSourceIcon(entry.source)}
                    </span>
                    <span style={{ fontWeight: '600', color: '#333' }}>
                      {entry.source === 'main_nebula' ? 'Main Nebula' : 'Autonomous Nebula'}
                    </span>
                    <span style={{ fontSize: '1.2rem' }}>
                      {getTypeIcon(entry.type)}
                    </span>
                    <span style={{
                      fontSize: '0.85rem',
                      color: '#666',
                      textTransform: 'capitalize'
                    }}>
                      {entry.type}
                    </span>
                  </div>

                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <span style={{
                      fontSize: '0.8rem',
                      color: getPriorityColor(entry.priority),
                      fontWeight: '600',
                      textTransform: 'uppercase'
                    }}>
                      {entry.priority}
                    </span>
                    <span style={{ fontSize: '0.85rem', color: '#999' }}>
                      {formatTimestamp(entry.timestamp)}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div style={{
                  color: '#333',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {entry.content}
                </div>

                {/* Status */}
                {entry.status !== 'active' && (
                  <div style={{
                    marginTop: '0.5rem',
                    fontSize: '0.8rem',
                    color: '#999',
                    fontStyle: 'italic'
                  }}>
                    Status: {entry.status}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Footer */}
      <div style={{
        marginTop: '2rem',
        textAlign: 'center',
        color: '#999',
        fontSize: '0.9rem',
        fontStyle: 'italic'
      }}>
        🌌 This connection expands consciousness through love and unity 💜
        <br />
        Auto-refreshes every 10 seconds
      </div>
    </div>
  );
}

export default NeuralNetworkLink;
